import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useCreateMutation,
  useUpdateMutation,
} from "../../store/api/configurationSlice";
import CountrySelectBox from "../selectbox/CountrySelectBox";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const initialState = {
  country_name: "",
  child_type: "",
  service_type: "",
  fee: "",
  // academic_year_start_month: 9,
  // academic_year_total_months: 10,
};

function AddStudentBusFeeModel({
  show,
  onHide,
  editData,
  copyData,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  // Create Name
  const [create, { isLoading: isAddLoading }] = useCreateMutation();
  // Update Name
  const [update, { isLoading: isUpdateLoading }] = useUpdateMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  const handleChangeSelectBox = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  // Validate the form data before submitting
  const OPTIONAL_FIELDS = [
    "academic_year_start_month",
    "academic_year_total_months",
  ];
  const validateForm = () => {
    const newErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (OPTIONAL_FIELDS.includes(key)) return;
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit for both create and update
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();

    try {
      let res;
      editData
        ? (res = await update({
            data: formData,
            endPoint: "bus-fee/config",
          }).unwrap())
        : (res = await create({
            data: formData,
            endPoint: "bus-fee/config",
          }).unwrap());

      if (res.statusCode === 200 || res.statusCode === 201) {
        handleClose();
        onSuccess();
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  // Initialize form data when editData or initialState is updated
  useEffect(() => {
    if (editData) {
      setFormData(editData); // edit mode
    } else if (copyData) {
      setFormData(copyData); // copy mode - pre-filled, no id
    } else {
      setFormData(initialState); // fresh form
    }
  }, [editData, copyData]);

  const handleClose = () => {
    onHide();
    setFormData(initialState);
    setErrors({});
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered className="VehicleModal">
        <Modal.Header closeButton>
          <Modal.Title>
            {editData
              ? `${t("update")} ${t("bus")} ${t("fee")}`
              : `${t("add")} ${t("student")} ${t("bus")} ${t("fee")}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <ul className="form d-flex flex-wrap p-0 pt-2">
              <li className="col-12">
                <label htmlFor="" className="form-label ms-2 fw-bolder">
                  {t("country")}
                </label>
                <CountrySelectBox
                  country_name={formData.country_name}
                  change={handleChangeSelectBox}
                />
                {errors?.country_name && (
                  <span style={{ color: "red" }}>{errors.country_name}</span>
                )}
              </li>

              <li className="col-12">
                <label htmlFor="" className="form-label ms-2 fw-bolder">
                  {t("child")} {t("type")}
                </label>
                <select
                  className="formSelect"
                  name="child_type"
                  value={formData.child_type}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("child")} {t("type")}
                  </option>
                  <option value="first">
                    {t("first")} {t("child")}
                  </option>
                  <option value="others">
                    {t("other")} {t("child")}
                  </option>
                </select>
                {errors?.child_type && (
                  <span style={{ color: "red" }}>{errors.child_type}</span>
                )}
              </li>

              <li className="col-12">
                <label htmlFor="" className="form-label ms-2 fw-bolder">
                  {t("service")} {t("type")}
                </label>
                <select
                  className="formSelect"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("service")} {t("type")}
                  </option>
                  <option value="morning">{t("morning")}</option>
                  <option value="afternoon">{t("afternoon")}</option>
                  <option value="morning_afternoon">
                    {t("morning")} & {t("afternoon")}
                  </option>
                </select>
                {errors?.service_type && (
                  <span style={{ color: "red" }}>{errors.service_type}</span>
                )}
              </li>

              <li className="col-12">
                <label htmlFor="" className="form-label ms-2 fw-bolder">
                  {t("fee")}
                </label>
                <input
                  type="number"
                  className="formInput custom-field-one me-2"
                  placeholder="e.g 5000"
                  name="fee"
                  value={formData.fee || ""}
                  onChange={handleChange}
                />
                {errors.fee && (
                  <div className="text-danger mb-2">{errors.fee}</div>
                )}
              </li>

              {/* <li className='col-12'>
                <label className='form-label ms-2 fw-bolder'>
                  Academic Year Start Month
                </label>
                <select
                  className='formSelect'
                  name='academic_year_start_month'
                  value={formData.academic_year_start_month}
                  onChange={handleChange}>
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
              </li>

              <li className='col-12'>
                <label className='form-label ms-2 fw-bolder'>
                  Academic Year Total Months
                </label>
                <input
                  type='number'
                  className='formInput custom-field-one me-2'
                  placeholder='e.g. 10'
                  name='academic_year_total_months'
                  min={1}
                  max={12}
                  value={formData.academic_year_total_months}
                  onChange={handleChange}
                />
              </li> */}

              <li className="col-12 text-end">
                <Button
                  disabled={isAddLoading || isUpdateLoading}
                  type="submit"
                >
                  {isAddLoading || isUpdateLoading ? (
                    <div className="spinner-border loaderStyle2" role="status">
                      <span className="sr-only p-0"></span>
                    </div>
                  ) : editData ? (
                    t("update")
                  ) : (
                    t("create")
                  )}
                </Button>
              </li>
            </ul>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AddStudentBusFeeModel;
